import {Component, OnInit, Renderer2} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {of} from 'rxjs';
import {nameof} from 'ts-simple-nameof';
import {QuestionDefinition} from '../../forms/form.types';
import {GenericFormGroupComponent} from '../../forms/generic-form-group.component';
import {CourseCacheService} from '../../shared/course-cache.service';
import {Roster, Student, StudentList} from '../../shared/interface/roster.interface';
import {RosterService} from '../../shared/roster.service';
import {AlertService} from '../../shared/services/alert.service';
import {browserRefresh} from '../../app.component';

@Component({
    selector: 'app-roster-add-student',
    templateUrl: './add-student.component.html',
    styleUrls: ['./add-student.component.scss']
})
export class AddStudentComponent extends GenericFormGroupComponent implements OnInit {
    newStudent: boolean = true;
    controls: {
        id: FormControl<number>;
        studentId: FormControl<string>;
        bannerFirstName: FormControl<string>;
        bannerLastName: FormControl<string>;
        emailAddress: FormControl<string>;
        gradeRule: FormControl<string>;
    };

    questions: {
        studentId: QuestionDefinition;
        bannerFirstName: QuestionDefinition;
        bannerLastName: QuestionDefinition;
        emailAddress: QuestionDefinition;
        gradeRule: QuestionDefinition;
    };

    constructor(
        public route: ActivatedRoute,
        public courseCacheService: CourseCacheService,
        public rosterService: RosterService,
        public alertService: AlertService,
        protected renderer2: Renderer2
    ) {
        super(renderer2);
    }

    termCode: string;
    crn: string;
    browserRefresh: boolean;

    // tempStudent: Partial<Student> = {};
    tempInstructorAddStudent: Array<Student>;

    ngOnInit(): void {
        this.browserRefresh = browserRefresh;

        if (!this.termCode || browserRefresh) {
            this.termCode = this.route.snapshot.paramMap.get('termCode');
        }
        if (!this.crn || browserRefresh) {
            this.crn = this.route.snapshot.paramMap.get('crn');
        }
        if (!this.rosterService.currentRoster || browserRefresh) {
            this.rosterService.get(this.termCode, this.crn).subscribe((response: Roster) => {
                this.rosterService.setCurrentRoster(response);
                this.rosterService
                    .instructorAddStudentList(this.rosterService.currentRoster)
                    .subscribe((response: StudentList) => {
                        if (response.list.length > 0) {
                            this.tempInstructorAddStudent = response.list;
                        }
                    });
            });
        }
        this.initControlsAndQuestions();


    }

    private initControlsAndQuestions() {
        this.controls = {
            id: new FormControl<number>(-1),
            studentId: new FormControl<string>(null),
            bannerFirstName: new FormControl<string>(null, Validators.required),
            bannerLastName: new FormControl<string>(null, Validators.required),
            emailAddress: new FormControl<string>(null, [Validators.required, Validators.email]),
            gradeRule: new FormControl<string>(null, Validators.required)
        };
        this.createFormGroup(this.controls);
        this.questions = {
            studentId: {
                input: {
                    discriminator: 'TEXT',
                    property: nameof<Student>((m) => m.studentId),
                    control: this.controls.studentId,
                    maxChars: 9
                }
            },
            bannerFirstName: {
                input: {
                    discriminator: 'TEXT',
                    property: nameof<Student>((m) => m.bannerFirstName),
                    label: 'First Name',
                    control: this.controls.bannerFirstName,
                    required: true,
                    maxChars: 255
                }
            },
            bannerLastName: {
                input: {
                    discriminator: 'TEXT',
                    property: nameof<Student>((m) => m.bannerLastName),
                    label: 'Last Name',
                    control: this.controls.bannerLastName,
                    required: true,
                    maxChars: 255
                }
            },
            emailAddress: {
                input: {
                    discriminator: 'TEXT',
                    property: nameof<Student>((m) => m.emailAddress),
                    label: 'Email',
                    control: this.controls.emailAddress,
                    required: true,
                    maxChars: 255,
                    errorTypes: [
                        {
                            code: 'email',
                            message: 'That does not appear to be a valid email address.'
                        }
                    ]
                }
            },
            gradeRule: {
                input: {
                    discriminator: 'SELECT',
                    property: nameof<Student>((m) => m.gradeRule),
                    label: 'Student Level',
                    control: this.controls.gradeRule,
                    required: true,
                    options$: of([
                        {
                            text: 'Undergraduate',
                            value: 'U'
                        },
                        {
                            text: 'Graduate',
                            value: 'G'
                        }
                    ])
                }
            }
        };
    }

    ngAfterViewInit(): void {

        this.loadInstructorAddedStudent();

    }

    loadInstructorAddedStudent(): void {
        this.rosterService
            .instructorAddStudentList(this.rosterService.currentRoster)
            .subscribe((response: StudentList) => {
                if (response.list.length > 0) {
                    this.tempInstructorAddStudent = response.list;
                }
            });
    }

    addStudent(): void {
        this.setSaving();
        this.rosterService.addOneStudent(this.rosterService.currentRoster, this.getFormValue()).subscribe({
            next: (response) => {
                this.setDoneSaving();
                if (response.list.length > 0) {
                    this.alertService.toastSuccess('Student Added.');
                    this.tempInstructorAddStudent = response.list;
                    this.clearAllValuesAndMarkPristine();
                    this.formGroup.markAsUntouched();
                    this.newStudent = true;
                } else {
                    this.alertService.toastWarn('Something went wrong adding the student.');
                }
            },
            error: (err) => {
                this.setDoneSaving();
                this.alertService.toastErrorObject(err);
            }
        });
    }

    updateStudent(): void {
        this.setSaving();
        this.rosterService.updateOneStudent(this.getFormValue()).subscribe({
            next: (response: StudentList) => {
                if (response.list.length > 0) {
                    this.tempInstructorAddStudent = response.list;
                    this.alertService.toastSuccess('Student information updated.');
                    this.clearAllValuesAndMarkPristine();
                    this.newStudent = true;
                } else {
                    this.alertService.toastWarn('Unable to update student');
                }
                this.setDoneSaving();
            },
            error: (err) => {
                this.setDoneSaving();

                this.alertService.toastErrorObject(err);
            }
        });
    }

    deleteStudent(student: Student): void {
        const alertText = student.bannerFirstName + ' ' + student.bannerLastName;
        this.alertService.confirm({
            title: 'Are you sure you want to remove this student?',
            text: alertText,
            callBack: {
                this: this,
                function: (result: boolean) => {
                    if (result === true) {
                        this.rosterService.deleteOneStudent(student).subscribe((response: StudentList) => {
                            this.tempInstructorAddStudent = response.list;
                            if (this.tempInstructorAddStudent.find((item: Student) => item.id === student.id)) {
                                this.alertService.toastWarn('Unable to delete student');
                            } else {
                                this.alertService.toastSuccess('Student has been deleted.');
                            }
                        });
                    }
                }
            }
        });
    }

    cancle(): void {
        this.newStudent = true;
        this.clearAllValuesAndMarkPristine();
        this.loadInstructorAddedStudent();
    }

    editStudent(student: Student): void {
        this.newStudent = false;
        this.controls.id.setValue(student?.id);
        this.controls.studentId.setValue(student?.studentId);
        this.controls.bannerFirstName.setValue(student?.bannerFirstName);
        this.controls.bannerLastName.setValue(student?.bannerLastName);
        this.controls.emailAddress.setValue(student?.emailAddress);
        this.controls.gradeRule.setValue(student?.gradeRule);
    }
}
